import React, { useCallback, useEffect, useState } from "react";

import { checkForUpdates, fetchSystemInfo } from "../../../api";
import { useNotify } from "../../../ToastContext";
import { usePermission } from "../../../UserContext";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function CopyableCommand({ command }) {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(command).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="gwUpdatesCommand">
      <pre className="gwUpdatesCommandText">{command}</pre>
      <button type="button" className="gwLicenseCopyBtn" onClick={onCopy}>
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

export function PlatformUpdatesPage() {
  const isAdmin = usePermission("admin");
  const notify = useNotify();
  const [current, setCurrent] = useState(null);
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);

  // Initial load: read current version from /api/system-info — no
  // phone-home required. The "Check for updates" button below is the
  // operator-initiated path that contacts controlseat.com.
  useEffect(() => {
    let cancelled = false;
    fetchSystemInfo()
      .then((info) => {
        if (!cancelled) setCurrent(info);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't read current version");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onCheck = useCallback(async () => {
    setChecking(true);
    setError(null);
    try {
      const result = await checkForUpdates();
      setCheckResult(result);
      if (result.reachable && !result.update_needed) {
        notify?.({ type: "success", title: "Up to date", message: "You're on the latest version." });
      } else if (result.reachable && result.update_needed) {
        notify?.({
          type: "info",
          title: "Update available",
          message: `Latest version: ${result.latest}`,
        });
      } else if (!result.reachable) {
        notify?.({
          type: "error",
          title: "Update server unreachable",
          message: result.error || "Couldn't reach the update server.",
        });
      }
    } catch (err) {
      setError(err?.message || "Check failed");
    } finally {
      setChecking(false);
    }
  }, [notify]);

  // One-shot forward-only upgrader. See deploy/onprem/update.sh.
  //
  // The path below assumes the standard Linux install at
  // /opt/controlseat. Custom installs override CS_INSTALL_DIR (see the
  // note below the command).
  const upgradeCommand = "sudo /opt/controlseat/update.sh";

  const currentVersion = current?.version || checkResult?.current || "—";

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Updates</div>
            <div className="gwPageSubtitle">
              Manage the version of Control Seat running on this gateway.
            </div>
          </div>
          {isAdmin && (
            <button
              type="button"
              className="gwPrimaryBtn"
              onClick={onCheck}
              disabled={checking}
            >
              {checking ? "Checking…" : "Check for updates"}
            </button>
          )}
        </div>

        <div className="gwCardSection">
          <div className="gwSectionTitle">Current version</div>
          <div className="gwCardSectionBody">
            <div className="gwUpdatesVersionRow">
              <div className="gwUpdatesVersionMain">{currentVersion}</div>
              {current?.commit && (
                <div className="gwUpdatesVersionMeta">
                  commit <code>{current.commit}</code>
                </div>
              )}
              {current?.build_time && (
                <div className="gwUpdatesVersionMeta">
                  built {formatDate(current.build_time)}
                </div>
              )}
            </div>
          </div>
        </div>

        {checkResult && checkResult.reachable && checkResult.update_needed && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Update available</div>
            <div className="gwCardSectionBody">
              <div className="gwUpdatesAvailable">
                <div className="gwUpdatesAvailableTitle">
                  {checkResult.latest}
                </div>
                {checkResult.published_at && (
                  <div className="gwUpdatesAvailableDate">
                    Released {formatDate(checkResult.published_at)}
                  </div>
                )}
                <div className="gwUpdatesAvailableActions">
                  {checkResult.release_url && (
                    <a
                      href={checkResult.release_url}
                      target="_blank"
                      rel="noreferrer"
                      className="gwLicenseCopyBtn"
                    >
                      Release notes ↗
                    </a>
                  )}
                  {checkResult.download_url && (
                    <a
                      href={checkResult.download_url}
                      target="_blank"
                      rel="noreferrer"
                      className="gwLicenseCopyBtn"
                    >
                      Download page ↗
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {checkResult && checkResult.reachable && !checkResult.update_needed && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Status</div>
            <div className="gwCardSectionBody">
              <div className="gwUpdatesUpToDate">
                You’re running the latest published version.
              </div>
            </div>
          </div>
        )}

        {checkResult && !checkResult.reachable && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Update server unreachable</div>
            <div className="gwCardSectionBody">
              <div className="gwAlert">
                <div className="gwAlertTitle">Couldn’t reach the update server</div>
                <div className="gwAlertBody">
                  {checkResult.error ? (
                    <code>{checkResult.error}</code>
                  ) : (
                    "Air-gapped installs can ignore this — you can still upgrade manually with the command below."
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Error</div>
            <div className="gwCardSectionBody">
              <div className="gwAlert">
                <div className="gwAlertTitle">Check failed</div>
                <div className="gwAlertBody">{error}</div>
              </div>
            </div>
          </div>
        )}

        <div className="gwCardSection">
          <div className="gwSectionTitle">How to upgrade</div>
          <div className="gwCardSectionBody">
            <div className="gwUpdatesNote">
              SSH into the gateway host and run the upgrade script. It pulls
              new images, quiesces the database-writing services, runs database
              migrations, activates those services plus the editor, and verifies
              their health.
              Your volumes — license, database, configuration, and TLS cert —
              all survive.
            </div>
            <CopyableCommand command={upgradeCommand} />

            <div className="gwUpdatesSubBlock">
              <div className="gwUpdatesSubhead">Recovery after migration</div>
              <div className="gwUpdatesNote">
                Database migrations are forward-only. If an update fails after
                migration, keep the writers stopped, repair the current
                release, and rerun the updater. Never start an older writer
                image against the migrated database.
              </div>
            </div>

            <div className="gwUpdatesSubBlock">
              <div className="gwUpdatesSubhead">Other install layouts</div>
              <dl className="gwUpdatesDefList">
                <dt>Custom Linux path</dt>
                <dd>
                  Installed somewhere other than <code>/opt/controlseat</code>?
                  Set <code>CS_INSTALL_DIR</code> to your install directory and
                  run its <code>update.sh</code>.
                </dd>
                <dt>macOS / Windows (dev)</dt>
                <dd>
                  Docker Desktop users run from the unpacked release directory
                  without <code>sudo</code> — <code>cd</code> there and run{" "}
                  <code>./update.sh</code>. The script detects its own location.
                </dd>
                <dt>Air-gapped</dt>
                <dd>
                  Mirror the GHCR images to your local registry and point{" "}
                  <code>.env</code> at the mirrored tags. The same script works.
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="gwCardSection">
          <div className="gwSectionTitle">Privacy</div>
          <div className="gwCardSectionBody">
            <div className="gwNotice">
              “Check for updates” is the only outbound request this gateway
              ever makes to Control Seat. It only fires when you click the
              button above. The runtime never phones home; license
              verification is fully offline.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PlatformUpdatesPage;
